/* eslint pinterest/no-translation-outside-of-function:0 */

import BackboneEventsShim from 'app/common/lib/BackboneEventsShim';
import errorUtil from 'app/common/lib/errorUtil';
import gapi from 'app/common/lib/Gapi';
import i18n from 'app/common/lib/i18n';
import initGoogleAuth2SDK from 'app/common/react/components/growth/unauth/signup/GoogleConnectButton/initGoogleAuth2SDK';

import timer from 'app/common/lib/timer';
import user from 'app/common/lib/User';

const facebookDialogLoader = () =>
  import(/* webpackChunkName: "FacebookDialogCommon" */ 'app/common/lib/FacebookDialog');
const facebookLoader = () =>
  import(/* webpackChunkName: "FacebookCommon" */ 'app/common/lib/Facebook');

/**
 * @constructor
 */
function SocialConnect() {}

/**
 * @type {SocialConnect}
 */
let socialConnectInstance;

Object.assign(SocialConnect.prototype, BackboneEventsShim);
Object.assign(SocialConnect.prototype, {
  /**
   * @param {string} network
   * @param {{success:(function(!Object)|undefined),
   *          error:(function(!Object)|undefined),
   *          complete:(function()|undefined),
   *          rerequest_facebook_email:boolean}} options
   */
  connect(network, options) {
    options = Object.assign(
      {},
      {
        success() {},
        error() {},
        complete() {},
      },
      options,
    );

    if (options.rerequest_facebook_email) {
      facebookLoader().then(({ default: Facebook }) => {
        // Use FB javascript SDK to make rerequest for an email
        Facebook.ensureInit((_fb) => {
          _fb.login(
            (response) => {
              const authResponse = response.authResponse;
              let grantedScopes, userId;
              if (authResponse) {
                grantedScopes = authResponse.grantedScopes;
                if (grantedScopes) {
                  grantedScopes = grantedScopes.split(',');
                } else {
                  grantedScopes = [];
                }
                userId = authResponse.userID;
              }

              const emailScope = grantedScopes.find((value) => value === 'email');

              const connectData = { id: userId };

              if (response.status === 'connected' && emailScope) {
                options.success(connectData);
              } else {
                options.error(connectData);
              }
              options.complete();
            },
            {
              scope: 'email',
              auth_type: 'rerequest',
              return_scopes: true,
            },
          );
        }, user);
      });
    } else if (network === 'gplus') {
      gapi.get((_gapi) => {
        initGoogleAuth2SDK().then(() => {
          _gapi.auth2
            .getAuthInstance()
            .grantOfflineAccess()
            .then((authResult) => {
              options.success({
                one_time_code: authResult.code,
                redirect_uri: 'postmessage',
              });
            })
            .catch((error) => {
              options.error(error);
            });
        });
      });
    } else {
      facebookDialogLoader().then(({ default: FacebookDialog }) => {
        const windowOptions = FacebookDialog.getWindowOptions();
        const c = window.open('/connect/' + network + '/', '', windowOptions);

        if (!c) {
          errorUtil.showError(i18n._('You must enable popups!'));
          options.error({ network, alertedUpstream: true });
          return;
        }

        // Periodically check for whether the window has been closed, in
        // case we don't have a successful connect event.
        const intervalId = timer.setInterval(() => {
          if (c.closed) {
            clearInterval(intervalId);
            this.handleComplete({
              network,
              error_message: 'Window closed.', // TODO: i18n
            });
          }
        }, 1000);

        // This will remove itself after the first trigger.
        this.once('connect:' + network, (connectData) => {
          clearInterval(intervalId);
          if (connectData.error_message) {
            options.error(connectData);
          } else {
            options.success(connectData);
          }
          options.complete();
        });
      });
    }
  },

  /**
   * Called by SocialConnectPopup when the social network connection has been established.
   * We intentionally pass JSON-encoded strings between windows instead of passing objects.
   * IE 7-8 have quirks passing objects between windows.
   * See: http://goo.gl/8DhCe
   *
   * @param {string} dataJson JSON-encoded data specific to the social network
   */
  complete(data) {
    this.handleComplete(data);
  },

  /**
   * Dispatch the social network connect result to interested listeners.
   *
   * @param {!Object} data Social network-specifc data
   */
  handleComplete(data) {
    const key = 'connect:' + data.network;
    const network = data.network;
    if (network === 'facebook') {
      const facebook_id = data.id;
      if (facebook_id && typeof facebook_id !== 'string') {
        data.error_message = 'Facebook id must be of string type';
      }
    }
    // Trigger all the callbacks.
    this.trigger(key, data);
  },
});

Object.assign(
  SocialConnect,
  /** @lends {SocialConnect} */ {
    /**
     * @return {SocialConnect}
     */
    instance() {
      if (!socialConnectInstance) {
        socialConnectInstance = new SocialConnect();
      }
      return socialConnectInstance;
    },

    // Available networks
    FACEBOOK: 'facebook',
    TWITTER: 'twitter',
    GOOGLE: 'google',
    YOUTUBE: 'youtube',
    GPLUS: 'gplus',
    MICROSOFT: 'microsoft',
    ETSY: 'etsy',
  },
);

if (!__SERVER__) {
  window.addEventListener('message', (e) => {
    const origin =
      window.location.protocol +
      '//' +
      window.location.hostname +
      (window.location.port ? ':' + window.location.port : '');
    if (e.origin !== origin) {
      return;
    }
    if (e.data.type === 'social-connect-complete') {
      SocialConnect.instance().complete(e.data);
    }
  });
}

export default SocialConnect;
